import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Archive, ArrowClockwise, ClockCounterClockwise, Cloud, Copy, FlowArrow, PaperPlaneTilt, PenNib, TrayArrowDown, UploadSimple, WarningCircle } from '@phosphor-icons/react';
import { ImmoButton, TabLeiste, type Tab } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { useDokumenteKopf, type ZuletztDatei } from '../../../lib/dokumente/useDokumenteKopf';
import { DokumenteWidgets } from './kopf/DokumenteWidgets';
import { supabase } from '../../../lib/supabase/client';
import { AktenschrankTab } from './aktenschrank/AktenschrankTab';
import { ZurAblageTab } from './ablage/ZurAblageTab';
import { SignierenTab } from './signieren/SignierenTab';
import { VorlagenTab } from './vorlagen/VorlagenTab';
import { RegelnTab } from './regeln/RegelnTab';
import { AltarchivTab } from './altarchiv/AltarchivTab';
import { PostausgangTab } from './post/PostausgangTab';
import { PostLogo } from '../../../components/epost/PostLogo';
import { ZuletztTab } from './zuletzt/ZuletztTab';

export type DokTab = 'akten' | 'neu' | 'ablage' | 'sign' | 'post' | 'vorl' | 'regeln' | 'alt';
const TAB_KEYS: readonly DokTab[] = ['akten', 'neu', 'ablage', 'sign', 'post', 'vorl', 'regeln', 'alt'];
const istTab = (v: string | null): v is DokTab => !!v && (TAB_KEYS as readonly string[]).includes(v);


/**
 * Dokumente (Handoff Dokumente §6): Kopf, 4 Widgets, Tabs (?tab=…). Mobil (§7): Titel 30 px, Tabs wischbar.
 */
export default function DokumenteSeite({ userProfile }: { userProfile?: { id?: string } & Record<string, unknown> }) {
  const navigate = useNavigate();
  const { id: pfadId } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const hochladenRef = useRef<(() => void) | null>(null);
  const { mobil } = useMobilModus();
  const { zustand, neuLaden } = useDokumenteKopf();
  const kopf = zustand.status === 'ok' ? zustand.kopf : null;
  const tab: DokTab = istTab(params.get('tab')) ? params.get('tab') as DokTab : 'akten';

  const setzeTab = useCallback((t: DokTab, extra: Record<string, string> = {}) => {
    setParams(p => {
      const n = new URLSearchParams();
      if (t !== 'akten') n.set('tab', t);
      // Objekt-Bezug im Aktenschrank behalten
      if (t === 'akten' && p.get('objekt')) n.set('objekt', p.get('objekt') as string);
      for (const [k, v] of Object.entries(extra)) n.set(k, v);
      return n;
    });
  }, [setParams]);
  const zuDatei = (d: ZuletztDatei) => setzeTab('akten', { ...(d.objekt_id ? { objekt: d.objekt_id } : {}), datei: d.gdrive_file_id });

  // /admin/dokumente/:id – Dokument-ID oder Drive-ID → Aktenschrank mit Objekt und Datei
  useEffect(() => {
    if (!pfadId) return;
    let aktiv = true;
    const istUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pfadId);
    const abfrage = supabase.from('dokumente').select('objekt_id, gdrive_file_id');
    (istUuid ? abfrage.eq('id', pfadId) : abfrage.eq('gdrive_file_id', pfadId)).limit(1).maybeSingle().then(({ data }) => {
      if (!aktiv) return;
      const ziel = new URLSearchParams();
      const datei = (data?.gdrive_file_id as string | null | undefined) ?? (istUuid ? null : pfadId);
      if (data?.objekt_id) ziel.set('objekt', data.objekt_id as string);
      if (datei) ziel.set('datei', datei);
      navigate(`/admin/dokumente${ziel.size ? `?${ziel.toString()}` : ''}`, { replace: true });
    });
    return () => { aktiv = false; };
  }, [pfadId, navigate]);
  const hochladen = () => { if (tab === 'akten' && hochladenRef.current) hochladenRef.current(); else setzeTab('akten'); };

  const tabs: readonly Tab<DokTab>[] = [
    { key: 'akten', label: 'Aktenschrank', icon: Archive },
    { key: 'neu', label: 'Zuletzt hinzugefügt', icon: ClockCounterClockwise },
    { key: 'ablage', label: 'Zur Ablage', icon: TrayArrowDown, zaehler: kopf?.ablage.offen },
    { key: 'sign', label: 'Signieren', icon: PenNib, zaehler: kopf?.signatur.wartend },
    { key: 'post', label: 'Postausgang', icon: PostLogo },
    { key: 'vorl', label: 'Vorlagen', icon: Copy },
    { key: 'regeln', label: 'Ablage-Regeln', icon: FlowArrow },
    { key: 'alt', label: 'Altarchiv', icon: Cloud },
  ];
  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Dokumente</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>Aktenschrank in Google Drive, Ablage aus E-Mails und digitale Signatur</span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={44} icon={PaperPlaneTilt} onClick={() => navigate('/admin/schreiben/neu')} style={{ boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 }}>Neues Schreiben</ImmoButton>
          <ImmoButton groesse={44} icon={PenNib} onClick={() => setzeTab('sign', { bearbeiten: 'neu' })} style={{ boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 }}>Neue Signatur</ImmoButton>
          <ImmoButton art="akzent" groesse={44} icon={UploadSimple} onClick={hochladen} title="In den geöffneten Ordner hochladen">Hochladen</ImmoButton>
        </div>
      </header>

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Übersicht konnte nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}
      {zustand.status === 'laedt' && (
        <section aria-busy="true" aria-label="Übersicht wird geladen" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 14 }}>
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 150, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </section>
      )}
      {kopf && (
        <DokumenteWidgets k={kopf} onAblage={() => setzeTab('ablage')} onSignieren={() => setzeTab('sign')} onAkten={() => setzeTab('akten')} onDatei={zuDatei} onZuletzt={() => setzeTab('neu')} />
      )}

      <TabLeiste<DokTab> ariaLabel="Bereiche der Dokumente" tabs={tabs} wert={tab} onChange={t => setzeTab(t)} wischbar={mobil} />

      <div role="tabpanel" className="min-w-0">
        {tab === 'akten' && <AktenschrankTab hochladenRef={hochladenRef} userId={typeof userProfile?.id === 'string' ? userProfile.id : null} />}
        {tab === 'neu' && <ZuletztTab onOeffnen={e => setzeTab('akten', { objekt: e.objekt_id, datei: e.gdrive_file_id })} />}
        {tab === 'sign' && <SignierenTab userProfile={userProfile} onVorlagen={() => setzeTab('vorl')} />}
        {tab === 'ablage' && (
          <ZurAblageTab userId={typeof userProfile?.id === 'string' ? userProfile.id : null}
            onOeffneInAkten={(objektId, dateiId) => setzeTab('akten', { ...(objektId ? { objekt: objektId } : {}), datei: dateiId })} />
        )}
        {tab === 'vorl' && (
          <VorlagenTab userId={typeof userProfile?.id === 'string' ? userProfile.id : null} onEditor={id => setzeTab('sign', { bearbeiten: id })} />
        )}
        {tab === 'post' && <PostausgangTab />}
        {tab === 'regeln' && <RegelnTab />}
        {tab === 'alt' && (
          <AltarchivTab userId={typeof userProfile?.id === 'string' ? userProfile.id : null}
            onOeffneInAkten={(objektId, dateiId) => setzeTab('akten', { ...(objektId ? { objekt: objektId } : {}), datei: dateiId })} />
        )}
      </div>
    </>
  );
}
